import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import type { RootState } from "../../../../store/redux/store/store";
import {
  CompanyGrossPayView, CompanyGrossPayInput, AmountCalculationType, GrossPayType, Taxability,
  isBasicSalaryComponent,
} from "../../../../models/payroll";
import { PayrollSalaryBaseMode, PAYROLL_BASIS_DEFAULTS } from "../../../../models/general";
import { GetCompanyGrossPays, AddCompanyGrossPay, EditCompanyGrossPay, DeleteCompanyGrossPay } from "../../../../api/Payroll/grossPay";
import { getCompanyRules } from "../../../../api/General/Company";
import Pagination from "../../../../components/Pagination";
import CompanyGrossPayDialog from "./CompanyGrossPayDialog";
import { clearedBasicWageConsequence } from "./basicWageConsequence";
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle } from "../../../../components/ui/Dialog";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../../../../components/ui/Table";
import Button from "../../../../components/ui/Button";
import { Checkbox } from "../../../../components/ui/Checkbox";
import { Spinner } from "../../../../components/ui/Spinner";
import AppIcon from "../../../../components/AppIcon";
import { useToast, ToastRenderer } from "../../../../hooks/useToast";
import { StatusBadge } from "../../../../components/ui/StatusBadge";
import { PageLayout } from "../../../../components/ui/PageLayout";
import { Check, X, DollarSign } from "lucide-react";

const GrossPayConfig = () => {
  const { t } = useTranslation(['payroll', 'common']);
  const company = useSelector((state: RootState) => state.user.company);
  const { toast, showToast, dismissToast } = useToast();

  const [salaryComponents, setSalaryComponents] = useState<CompanyGrossPayView[]>([]);
  const [fixedSalaryComponents, setFixedSalaryComponents] = useState<CompanyGrossPayView[]>([]);
  const [filteredSalaryComponents, setFilteredSalaryComponents] = useState<CompanyGrossPayView[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [count, setCount] = useState(10);
  const [offset, setOffset] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState<"add" | "edit">("add");
  const [currentInput, setCurrentInput] = useState<CompanyGrossPayInput>({
    id: 0,
    name: "",
    amountCalculationType: AmountCalculationType.EmployeeSpecificAmount,
    type: GrossPayType.Allowance,
    taxability: Taxability.Exempted,
    proofRequired: false,
    amount: 0.0,
    formulaPercentage: 0.0,
    companyGrossPayForFormulaId: 0,
    // The ONE flag starts explicitly false on an add.
    isBasicSalary: false,
    includedInWps: true,
  });
  const [editId, setEditId] = useState(0);
  const [deleteId, setDeleteId] = useState(0);
  /**
   * 🔴 CO-163 — the delete path's acknowledgement gate, the twin of the one in the edit dialog.
   * Deleting the ONLY flagged component reaches exactly the same zero-flag state as unticking it,
   * so it is held behind the same explicit tick. It is reset every time the dialog opens on a new
   * row (see setDeleteId call sites) so a tick can never carry over to the next delete.
   */
  const [deleteAcknowledged, setDeleteAcknowledged] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({ type: "", taxability: "" });
  const [amountCalculation, setAmountCalculation] = useState("");
  const [hideFormula, setHideFormula] = useState(false);
  const [hideAmount, setHideAmount] = useState(false);
  /**
   * CO-163 — the tenant's configured salary base modes, read so the dialog can state the CONSEQUENCE
   * of clearing the statutory flag rather than a generic caution. Under
   * {@link PayrollSalaryBaseMode.BasicOnly} there is no fallback rung, so removing the last flagged
   * component makes overtime and gratuity pay ZERO (CO-244). Null while unknown — the dialog then
   * falls back to the unqualified warning rather than pretending the base is safe.
   */
  const [baseModes, setBaseModes] = useState<{ overtime: PayrollSalaryBaseMode; eos: PayrollSalaryBaseMode } | null>(null);

  const amountCalculationOptions = ['Employee Specific Amount', 'Formula Based Amount', 'Fixed Amount'];

  useEffect(() => {
    fetchData();
  }, []);

  // CO-163: base modes are read once, separately from the component list, and a failure is silent
  // on purpose — it must never block the screen. `null` simply means "consequence unknown", which
  // the dialog renders as the unqualified warning.
  useEffect(() => {
    const companyId = company?.id;
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const rules = await getCompanyRules(companyId);
        if (cancelled || !rules?.payload) return;
        setBaseModes({
          overtime: rules.payload.payrollOvertimeBaseMode ?? PAYROLL_BASIS_DEFAULTS.overtimeBaseMode,
          eos: rules.payload.payrollEosBaseMode ?? PAYROLL_BASIS_DEFAULTS.eosBaseMode,
        });
      } catch {
        // Deliberately swallowed — see above.
      }
    })();
    return () => { cancelled = true; };
  }, [company?.id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const components = await GetCompanyGrossPays();
      const fixedComponents = components.payload.filter((c: CompanyGrossPayView) => c.amountCalculationType === AmountCalculationType.FixedAmount || c.amountCalculationType === AmountCalculationType.EmployeeSpecificAmount);
      setSalaryComponents(components.payload);
      setFilteredSalaryComponents(components.payload);
      setFixedSalaryComponents(fixedComponents);
      setLoading(false);
    } catch (ex) {
      console.log(ex);
      setLoading(false);
    }
  };

  const filterData = (newState: any) => {
    setFilter((prevState) => ({ ...prevState, ...newState }));
    setFilteredSalaryComponents(
      salaryComponents.filter(
        (u) =>
          (!newState.type || u.type?.toLowerCase().includes(newState.type.toLowerCase())) &&
          (!newState.taxability || u.taxability?.toLowerCase().includes(newState.taxability.toLowerCase()))
      )
    );
  };

  const handleOpenDialog = (type: "add" | "edit", input?: CompanyGrossPayInput, id?: number) => {
    setDialogType(type);
    if (type === "edit" && input && id !== undefined) {
      setCurrentInput(input);
      setEditId(id);
    } else {
      setCurrentInput({
        id: 0,
        name: "",
        amountCalculationType: AmountCalculationType.EmployeeSpecificAmount,
        type: GrossPayType.Allowance,
        taxability: Taxability.Exempted,
        proofRequired: false,
        amount: undefined,
        formulaPercentage: undefined,
        companyGrossPayForFormulaId: undefined,
        isPrimary: false,
        isBasicSalary: false,
        includedInWps: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  // CO-163: every component that currently carries the statutory basic-wage flag, excluding the row
  // being edited. The invariant says at most one, so this is normally a 0- or 1-element list — but a
  // tenant that predates the invariant can hold several, and the UI has to be able to say so rather
  // than silently pick the first.
  const otherBasicSalaryComponents = salaryComponents.filter(
    (c) => isBasicSalaryComponent(c) && c.id !== currentInput.id
  );
  // The full divergence picture for the page-level diagnostic (below the header).
  const flaggedComponents = salaryComponents.filter(isBasicSalaryComponent);

  // ── CO-163: the DELETE path is the second route to "no statutory basic wage" ──────────────────
  // Archiving the flagged component reaches the same end state as unticking it in the edit dialog,
  // so the confirmation has to read the tenant's base modes and gate the button the same way. It
  // previously did neither: on a BasicOnly tenant it showed the soft "the amounts change" copy and
  // the Delete button was live from the moment the dialog opened.
  const componentBeingDeleted = salaryComponents.find((c) => c.id === deleteId) ?? null;
  /** Deleting the ONLY flagged component — the destructive case, and the one that can zero pay. */
  const deletingLastBasicWage = isBasicSalaryComponent(componentBeingDeleted) && flaggedComponents.length === 1;
  /** Deleting one of SEVERAL flagged components: the basic-wage total shrinks, it does not vanish. */
  const deletingOneOfSeveral = isBasicSalaryComponent(componentBeingDeleted) && flaggedComponents.length > 1;
  const deleteConsequence = clearedBasicWageConsequence(t, baseModes?.overtime ?? null, baseModes?.eos ?? null);

  const handleSubmit = async (input: CompanyGrossPayInput) => {
    setLoading(true);
    try {
      const result = dialogType === "add"
        ? await AddCompanyGrossPay(input)
        : await EditCompanyGrossPay(input);

      if (result?.errors?.length) {
        result.errors.forEach((err) => { if (err?.description) showToast(err.description, 'error'); });
        setLoading(false);
        return;
      }

      handleCloseDialog();
      await fetchData();

      // 🔴 CO-163: the outcome comes from the SERVER's response, not from pre-save client state.
      // The old version computed the demoted component from `salaryComponents` as it stood BEFORE
      // the call, fired `fetchData()` without awaiting it, and showed the toast regardless of what
      // the server actually did — so it announced a demotion on saves where the server moved
      // nothing (it only moves on a false -> true transition), and stayed silent on the destructive
      // direction entirely. These two flags are computed server-side inside the same transaction.
      const saved = result?.payload;
      const demoted = saved?.demotedBasicSalaryComponents ?? [];
      if (demoted.length > 0) {
        showToast(
          t('payroll:{{name}} is no longer the basic salary component.', { name: demoted.join(', ') }),
          'warning'
        );
      }
      if (saved?.clearedLastBasicSalaryComponent) {
        showToast(
          t('payroll:No component carries the basic salary any more. Overtime, end of service and pension no longer have a configured basic salary.'),
          'warning'
        );
      }
      setLoading(false);
    } catch (error: any) {
      console.log(error);
      if (error.errors && error.errors.length > 0) {
        error.errors.forEach((err: any) => {
          if (err.description)
            showToast(err.description, 'error');
        });
      }
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    // Belt and braces — the button is disabled, but the destructive delete must never slip through.
    if (deletingLastBasicWage && !deleteAcknowledged) return;
    setLoading(true);
    try {
      const result = await DeleteCompanyGrossPay(deleteId);

      if (result?.errors?.length) {
        result.errors.forEach((err) => { if (err?.description) showToast(err.description, 'error'); });
        setLoading(false);
        return;
      }

      setDeleteId(0);
      setDeleteAcknowledged(false);
      await fetchData();

      // 🔴 CO-163: the outcome comes from the SERVER's response, exactly as it does on save. The
      // delete path used to report nothing at all — it awaited a bare `true`, so HR was never told
      // that the company had just been left with no statutory basic wage.
      if (result?.payload?.clearedLastBasicSalaryComponent) {
        showToast(
          t('payroll:No component carries the basic salary any more. Overtime, end of service and pension no longer have a configured basic salary.'),
          'warning'
        );
      }
      setLoading(false);
    } catch (ex: any) {
      console.log(ex);
      if (ex && ex.status === 406) {
        showToast(t("payroll:Cannot delete salary component because it is assigned to users") ?? "", 'error');
      }
      setLoading(false);
    }
  };

  const onPageChange = (page: number): void => {
    setCurrentPage(page);
    setOffset((page - 1) * count);
  };

  return (
    <PageLayout
      title={t('payroll:Gross Pays')}
      description={t('payroll:Manage compensation & allowance components')}
      icon={DollarSign}
      accentColor="emerald"
      headerActions={(
        <Button
          variant="outline"
          iconName="PlusCircle"
          onClick={() => handleOpenDialog('add')}
          data-testid="add-type-btn"
        >
          {t('payroll:Add Gross Pay')}
        </Button>
      )}
    >
      <div className="flex flex-col gap-5 flex-1">
      {/*
        🔴 CO-163 — THE ALREADY-DIVERGENT TENANT.
        The single-flag invariant is enforced on every write path from now on, but it does not
        retro-fix a company that entered this state before it existed (prod company 30 did). With 2+
        flagged components BasicSalaryResolver rung 1 SUMS them, so the GOSI/GPSSA/CCHI/EOS/overtime
        base is the total of several lines and every one of them just wears the same badge. HR cannot
        fix what it cannot see, so the state is named here, with the components and the remedy.
      */}
      {!loading && flaggedComponents.length > 1 && (
        <div
          role="alert"
          data-testid="basic-wage-divergence"
          className="flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive"
        >
          <AppIcon name="AlertTriangle" size={16} className="mt-0.5 shrink-0" />
          <div className="max-w-[80ch] space-y-1.5">
            <p className="font-medium">
              {/* `total`, not `count` — `count` makes i18next resolve plural sub-keys
                  (`…_one` / `…_other`) that these sentence-shaped keys do not define. */}
              {t('payroll:{{total}} components are flagged as the basic salary.', { total: flaggedComponents.length })}
            </p>
            <p>
              {t('payroll:Only one component should carry it. While more than one does, overtime, end of service and pension are calculated from the SUM of {{names}}. Edit the components that should not be the basic salary and clear the flag on each.', { names: flaggedComponents.map((c) => c.name).join(', ') })}
            </p>
          </div>
        </div>
      )}

      {/* Table */}
      <Table aria-label="gross-pays-table" data-testid="settings-table">
        <TableHeader>
          <TableRow>
            <TableHead>{t('payroll:Component Name')}</TableHead>
            <TableHead>{t('payroll:Component Type')}</TableHead>
            <TableHead>{t('payroll:Taxability')}</TableHead>
            <TableHead>{t('payroll:Proof Required')}</TableHead>
            <TableHead>{t('common:includedInWps')}</TableHead>
            <TableHead>{t('payroll:Amount')}</TableHead>
            <TableHead>{t('payroll:Formula')}</TableHead>
            <TableHead>{t('common:Actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {!loading ? (
            filteredSalaryComponents
              .slice(offset, Math.min(offset + count, filteredSalaryComponents.length))
              .map((row) => {
                const formulaDisplay = row.amountCalculationType === AmountCalculationType.FormulaBasedAmount
                  ? `${row.formulaPercentage}% ${row.companyGrossPayForFormula?.name}`
                  : '';
                return (
                  <TableRow key={row.id}>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <span className="text-sm font-semibold text-foreground">{row.name}</span>
                        <div className="flex flex-wrap items-center gap-1">
                          {/* ONE badge for the ONE flag. It decides each employee's own salary
                              amount AND the GOSI/GPSSA/CCHI/EOS/overtime/WPS base, so which
                              component holds it has to be readable at a glance. */}
                          {isBasicSalaryComponent(row) && (
                            <StatusBadge variant="indigo" size="sm">
                              {t('payroll:Basic salary component')}
                            </StatusBadge>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{t(`payroll:${row.type}`)}</TableCell>
                    <TableCell className="text-sm">{t(`payroll:${row.taxability}`)}</TableCell>
                    <TableCell className="text-sm">
                      <StatusBadge variant={row.proofRequired ? 'warning' : 'neutral'} size="sm">
                        {row.proofRequired ? t('common:Yes') : t('common:No')}
                      </StatusBadge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {row.includedInWps ? (
                        <Check size={16} className="text-green-600" />
                      ) : (
                        <X size={16} className="text-gray-400" />
                      )}
                    </TableCell>
                    <TableCell className="text-sm tabular-nums">
                      {row.amount && row.amount !== 0 ? row.amount : ''}
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{formulaDisplay}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={String(t('common:Edit'))}
                          onClick={() => handleOpenDialog('edit', row, row.id)}
                        >
                          <AppIcon name="Pencil" size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={String(t('common:Delete'))}
                          onClick={() => { setDeleteAcknowledged(false); setDeleteId(row.id); }}
                        >
                          <AppIcon name="Trash2" size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
          ) : (
            <TableRow>
              <TableCell colSpan={8} className="text-center py-6">
                <Spinner size="md" />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <CompanyGrossPayDialog
        open={openDialog}
        onClose={handleCloseDialog}
        onSave={handleSubmit}
        input={currentInput}
        fixedSalaryComponents={fixedSalaryComponents}
        otherBasicSalaryComponents={otherBasicSalaryComponents}
        overtimeBaseMode={baseModes?.overtime ?? null}
        eosBaseMode={baseModes?.eos ?? null}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteId !== 0} onOpenChange={(open) => { if (!open) { setDeleteId(0); setDeleteAcknowledged(false); } }}>
        <DialogContent size="sm">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <AppIcon name="Trash2" size={18} className="text-primary" />
              <DialogTitle>{t('payroll:Delete Gross Pay')}</DialogTitle>
            </div>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-muted-foreground">
              {t('payroll:Are you sure you want to delete this component?')}
            </p>
            {/*
              🔴 CO-163 / CO-244 — DELETE IS THE OTHER ROUTE TO A ZERO STATUTORY BASE.
              This block used to be a single paragraph that never read `baseModes`, so a BasicOnly
              tenant — the one where the base really does become 0 — was shown the soft "the amounts
              change from the next calculation" copy, while the edit dialog reached by unticking the
              SAME flag said "calculate as ZERO". Same end state, two different promises. The
              consequence sentence now comes from `clearedBasicWageConsequence`, shared with that
              dialog, and the destructive case is acknowledgement-gated exactly like the untick.
            */}
            {deletingOneOfSeveral && (
              <div className="mt-3 flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                <AppIcon name="AlertTriangle" size={16} className="mt-0.5 shrink-0" />
                <p className="max-w-[70ch]">
                  {t('payroll:This component is flagged as the basic salary. Deleting it removes it from the basic-salary total, so overtime, end of service and pension change from the next calculation.')}
                </p>
              </div>
            )}
            {deletingLastBasicWage && (
              <div
                role="alert"
                data-testid="delete-basic-wage-clear-warning"
                className="mt-3 flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive"
              >
                <AppIcon name="AlertTriangle" size={16} className="mt-0.5 shrink-0" />
                <div className="max-w-[70ch] space-y-2">
                  <p className="font-medium">
                    {t('payroll:This is the only component flagged as the basic salary. Deleting it leaves the company with none.')}
                  </p>
                  <p>{deleteConsequence}</p>
                  <Checkbox
                    id="ack-delete-basic-wage"
                    checked={deleteAcknowledged}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDeleteAcknowledged(e.target.checked)}
                    label={t('payroll:I understand this leaves no basic salary component configured.')}
                  />
                </div>
              </div>
            )}
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setDeleteId(0); setDeleteAcknowledged(false); }}
              disabled={loading}
            >
              {t('common:Cancel')}
            </Button>
            {/* CO-163: deleting the last statutory flag is gated on the tick above, exactly as the
                equivalent untick is in the edit dialog. */}
            <Button
              variant="destructive"
              onClick={handleDelete}
              loading={loading}
              disabled={deletingLastBasicWage && !deleteAcknowledged}
              data-testid="confirm-delete-btn"
            >
              {deletingLastBasicWage ? t('payroll:Delete without a basic salary component') : t('common:Delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Pagination
        currentPage={currentPage}
        totalPages={Math.ceil(salaryComponents.length / count)}
        onPageChange={onPageChange}
      />

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
      </div>
    </PageLayout>
  );
};

export default GrossPayConfig;
